import React from 'react';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FixErrorButton } from '../fix-error-button/index.tsx';
import {
	useIsReadyForProactive,
	useDebuggerStatus,
	useJqlErrors,
	useJqlEditorOnSearch,
	useJqlQuery,
	useErrorComponentTestId,
	useErrorComponentValidationId,
} from '../../controllers/jql-debugger-data/index.tsx';
import messages from './messages.tsx';

type Props = {
	entryPointActions: EntryPointActions;
};

export function ErrorSection(props: Props) {
	const { entryPointActions } = props;
	const { formatMessage } = useIntl();
	const [query] = useJqlQuery();
	const [status] = useDebuggerStatus();
	const [jqlErrors] = useJqlErrors();
	const [onSearch] = useJqlEditorOnSearch();
	const [errorComponentTestId] = useErrorComponentTestId();
	const [errorComponentValidationId] = useErrorComponentValidationId();
	const [isReadyForProactive] = useIsReadyForProactive();

	// The Fix error button should be shown when:
	// The user has not invoked any action to make the experience ready for proactive, or
	// the user returns from dismissing a proactive AI result
	const showFixErrorButton = fg('jql-fixer-analytics-fixes')
		? status === 'INIT' && !isReadyForProactive
		: status === 'RESULT_DISCARDED' || (status === 'INIT' && !isReadyForProactive);

	return (
		<Inline alignBlock="center" spread="space-between" space="space.200" xcss={containerStyles}>
			<Inline alignBlock="start" spread="space-between" space="space.100">
				<Box as="h5" xcss={jqlErrorHeadingStyles}>
					<StatusErrorIcon color={token('color.icon')} label="" />
					<Text weight="bold">
						{formatMessage(
							jqlErrors?.length > 1
								? messages.jqlDebuggerHeadingPluralNonFinal
								: messages.jqlDebuggerHeadingNonFinal,
						)}
					</Text>
				</Box>

				<Box testId={errorComponentTestId}>
					<span role="alert" id={errorComponentValidationId}>
						<Box as="ul" xcss={errorBoxStyles} testId="jql-debugger.ui.error-section.error-list">
							{jqlErrors.map((error) => (
								<Box as="li" key={error} xcss={listItemStyle}>
									<Text color="color.text.accent.red">{error}</Text>
								</Box>
							))}
						</Box>
					</span>
				</Box>
			</Inline>
			<Box xcss={headerEndStyles}>
				{showFixErrorButton && (
					<FixErrorButton
						testId="jql-debugger.ui.error-section.trigger-button"
						jqlErrors={jqlErrors}
						onSearch={onSearch}
						query={query}
						entryPointActions={entryPointActions}
					/>
				)}
			</Box>
		</Inline>
	);
}

const containerStyles = xcss({
	minHeight: token('space.400'),
});

const errorBoxStyles = xcss({
	flex: 1,
	marginBlockStart: '0',
	paddingInlineStart: 'space.025',
});

const headerEndStyles = xcss({
	alignSelf: 'flex-start',
});

const listItemStyle = xcss({
	listStyle: 'none',
});

const jqlErrorHeadingStyles = xcss({
	display: 'flex',
	whiteSpace: 'nowrap',
	alignItems: 'center',
	gap: 'space.075',
});
